'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, Dialog } from 'src/components';
import { deleteAccountFolder } from 'src/api/accountFolders/accountFoldersApi';
import { useNotification } from 'src/context/notification';
import type { AccountFolder } from 'src/api/redux/accountFolders';

interface DeleteFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  folderToDelete: AccountFolder | null;
  onDeleteSuccess?: () => void;
}

const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({
  isOpen,
  onClose,
  folderToDelete,
  onDeleteSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { setNotification } = useNotification();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!folderToDelete) return;

    setIsDeleting(true);

    try {
      await deleteAccountFolder(folderToDelete.id.toString());

      setNotification({
        title: 'Success',
        message: `Folder "${folderToDelete.label}" has been deleted`,
        type: 'success',
      });

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error deleting folder:', error);
      setNotification({
        title: 'Error',
        message: error?.error_message || 'Failed to delete folder',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="delete-folder-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="delete-folder-dialog-title"
            className="text-neutral-800 mb-4"
          >
            Are you sure you want to delete the &quot;
            {folderToDelete?.label}
            &quot; folder? This action cannot be undone.
          </Typography>
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="delete-folder-dialog-cancel-button"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDelete}
              className="px-4"
              data-testid="delete-folder-dialog-confirm-button"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteFolderDialog;
